<template>
  <base-form-wrapper
    :id="formId"
    :title="$t('details.finance-quote')"
    :icon="icon"
  >
    <div class="quote-form">
      <finance-quote-radio
        v-if="paymentType"
        :active-option="activeOption"
        :disable-p-c-p="!appliesToPCP"
        @paymentTypeChange="paymentChangeHandler($event)"
      />
      <template v-if="paymentType === availableOptions.PCP">
        <div class="quote-field__wrapper">
          <div class="quote-field">
            <div>
              <span class="quote-field__description">
                {{ $t("shared.term") }}:
              </span>
              {{
                `${financeOptions.numberOfRepayments} ${$t("shared.months")}`
              }}
            </div>
            <base-tooltip dark>{{
              $t("finance-quote.terms-tooltip")
            }}</base-tooltip>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="quote-field__wrapper">
          <base-select
            :placeholder="
              `${$t('shared.term')}: ${hpOptions.defaultHpTerm} ${$t(
                'shared.months'
              )}`
            "
            :options="hpOptions.hpTerms"
            class="form-select quote-form__select"
            :active="
              hpTerm && `${$t('shared.term')}: ${hpTerm} ${$t('shared.months')}`
            "
            @update-store="hpTermsChangeHandler($event)"
          />
        </div>
      </template>
      <div class="quote-field__wrapper quote-field__wrapper--label">
        <label
          for="value"
          :class="[
            'quote-field',
            'quote-field__label',
            { 'quote-field--error': errorMessage }
          ]"
        >
          <div style="display: flex;">
            <span class="quote-field__description">
              {{ $t("finance-quote.deposit-trade") }} :
            </span>
            <span
              :class="[
                'quote-field__currency',
                {
                  'quote-field__currency--grey':
                    depositValue == calculateDepositValue
                }
              ]"
              >&euro;</span
            >
            <input
              id="value"
              v-model="shownValue"
              name="Desposit value"
              :placeholder="
                depositValue == calculateDepositValue &&
                  `${calculateDepositValue}`
              "
              type="text"
              class="quote-field__value"
              @keyup="formatMoney"
              @keyup.enter="$event.currentTarget.blur()"
              @blur="calculateQuote"
            />
          </div>
        </label>
        <base-validation-error
          v-if="errorMessage"
          :has-error="submitStatus === apiResponseMessages.ERROR"
        >
          <div v-for="(message, type) in errorMessage" :key="type">
            {{ errorMessage[type].join() }}
          </div>
        </base-validation-error>
        <div v-else class="quote-field__instruction">
          {{ $t("finance-quote.input-instruction") }}
        </div>
      </div>
      <div class="quote-info">
        <div class="quote-info__item">
          <div class="quote-info__number">
            {{ calculatedData && fixedApr }}%*
            <base-tooltip class="quote-info__tooltip" dark>
              {{ $t("finance-quote.apr") }}
            </base-tooltip>
          </div>
          <div class="quote-info__text">
            {{ $t("finance-quote.your-fixed-annual-percentage-rate") }}
          </div>
        </div>
        <div
          v-if="paymentType === availableOptions.PCP"
          class="quote-info__item"
        >
          <div class="quote-info__number">
            &euro;{{
              calculatedData &&
                roundUp({ number: calculatedData.gmfv, decimalPlaces: 0 })
            }}
            <base-tooltip class="quote-info__tooltip" dark>
              {{ $t("finance-quote.gmfv") }}</base-tooltip
            >
          </div>
          <div class="quote-info__text">
            {{ $t("finance-quote.gmfv++") }}
          </div>
        </div>
      </div>
      <div class="finance-quote__price">
        <base-price-badges
          v-if="calculatedData"
          class="quote-badge"
          single-badge="monthly"
          :monthly-price="calculatedData.monthly_price"
        />
        <a
          class="finance-quote__link"
          :href="!errorMessage && iFrameLink"
          target="_blank"
        >
          <base-button class="finance-quote__submit">
            {{ $t("details.apply-for-finance") }}
          </base-button>
        </a>
      </div>
    </div>
    <div class="quote-terms">
      <div class="quote-terms__conditions">
        <h2>Terms &amp; Conditions</h2>
        <ul v-if="calculatedData" class="terms-list">
          <li class="terms-list__item">
            {{
              $t("finance-quote.retail-price", {
                price: carPrice,
                currency: "€"
              })
            }}
          </li>
          <li class="terms-list__item">
            {{
              $t("finance-quote.type-of-finance-agreement", {
                type: calculatedData.type
              })
            }}
          </li>
          <li class="terms-list__item">
            {{
              $t("finance-quote.number-of-repayments", {
                months: calculatedData.months
              })
            }}
          </li>
          <li
            v-if="paymentType === availableOptions.PCP"
            class="terms-list__item"
          >
            {{
              `
            ${$t(
              "finance-quote.plus-an-optional-final-payment-of"
            )} - &euro;${formatCurrency(
                roundUp({
                  number: calculatedData.gmfv,
                  decimalPlaces: 2
                }),
                " "
              )}`
            }}
          </li>
          <li
            v-if="paymentType === availableOptions.PCP"
            class="terms-list__item"
          >
            {{
              $t("finance-quote.term", {
                term: "37"
              })
            }}
          </li>
          <li class="terms-list__item">
            {{
              `
            ${$t("finance-quote.finance-amount")} - &euro;
              ${formatCurrency(calculatedData.finance_amount, " ")}
              `
            }}
          </li>
          <li class="terms-list__item">
            {{
              `
              ${$t("finance-quote.total-cost-of-credit")} - &euro;
              ${formatCurrency(
                roundUp({
                  number: calculatedData.total_cost_of_credit,
                  decimalPlaces: 2
                }),
                " "
              )}
              `
            }}
          </li>
          <li class="terms-list__item">
            {{
              $t("finance-quote.annual-percentage-rate", {
                percentage: fixedApr
              })
            }}
          </li>
        </ul>
      </div>
      <div class="quote-terms__text">
        {{ termsText }}

        <div v-for="(warning, index) in warningText" class="quote-terms__warning" :key="index">
          <b>{{$t("finance-quote.warning") }}</b> {{ warning }}
        </div>
      </div>
    </div>
  </base-form-wrapper>
</template>

<script>
import BaseFormWrapper from "@/components/ui/BaseFormWrapper/BaseFormWrapper.vue";
import FinanceQuoteRadio from "@/components/details/FinanceQuoteRadio/FinanceQuoteRadio.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import BaseSelect from "@/components/ui/BaseSelect/BaseSelect.vue";
import BasePriceBadges from "@/components/ui/BasePriceBadges/BasePriceBadges.vue";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import BaseValidationError from "@/components/ui/BaseValidationError/BaseValidationError.vue";
import FinanceQuoteIcon from "@/assets/images/finance-quote.svg";
import {
  usedCarsAPI,
  CALCULATE_FINANCE_QUOTE,
  SEND_FINANCE_QUOTE
} from "@/api";
import { formatCurrency, roundUp, getFormattedPresentDate } from "@/utils";

import {
  financeOptions,
  apiResponseMessages,
  hpOptions
} from "@/constants/financeQuote";
import debounce from "lodash.debounce";

export default {
  name: "FinanceQuote",
  components: {
    BaseFormWrapper,
    FinanceQuoteRadio,
    BaseTooltip,
    BaseSelect,
    BasePriceBadges,
    BaseButton,
    BaseValidationError
  },
  props: {
    carId: {
      type: Number,
      required: true
    },
    carPrice: {
      type: Number,
      required: true
    },
    monthlyPrice: {
      type: Number,
      required: true
    },
    formId: {
      type: String,
      required: true
    },
    carYear: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      paymentType: null,
      depositValue: null,
      submitStatus: null,
      errorMessage: null,
      calculatedData: null,
      shownValue: null,
      hpTerm: null,
      iFrameLink: null,
      debouncedCalcQuote: null
    };
  },
  computed: {
    icon() {
      return FinanceQuoteIcon;
    },
    deposit() {
      return this.depositValue ? this.depositValue.split(",").join("") : 0;
    },
    financeOptions() {
      return financeOptions;
    },
    fixedApr() {
      return this.calculatedData.apr * 100;
    },
    formatCurrency() {
      return formatCurrency;
    },
    termsText() {
      return this.$t(
        this.paymentType === this.availableOptions.PCP
          ? "finance-quote.pcp-terms"
          : "finance-quote.hp-terms",
        {
          date: getFormattedPresentDate()
        }
      );
    },
    warningText() {
      return this.$t(
        this.paymentType === this.availableOptions.PCP
          ? "finance-quote.pcp-terms-warning"
          : "finance-quote.hp-terms-warning"
      );
    },
    availableOptions() {
      return financeOptions.availableOptions;
    },
    apiResponseMessages() {
      return apiResponseMessages;
    },
    calculateDepositValue() {
      const rawDepositValue =
        this.carPrice * this.financeOptions.defaultDepositOfCarValuePrecentage;

      return formatCurrency(
        roundUp({ number: rawDepositValue, decimalPlaces: 0 })
      );
    },
    roundUp() {
      return roundUp;
    },
    hpOptions() {
      return hpOptions;
    },
    prepareData() {
      return {
        type: this.paymentType,
        months:
          this.paymentType === this.availableOptions.PCP
            ? this.financeOptions.numberOfRepayments
            : this.hpTerm || this.hpOptions.defaultHpTerm,
        deposit: this.deposit
      };
    },
    appliesToPCP() {
      const minYearForPCP = new Date().getFullYear() - 3;
      return this.carYear >= minYearForPCP;
    },
    activeOption() {
      return this.appliesToPCP ? this.paymentType : this.availableOptions.HP;
    }
  },
  watch: {
    depositValue() {
      this.debouncedCalcQuote();
    }
  },
  mounted() {
    const { HP } = this.availableOptions;
    this.paymentType = HP;
    this.depositValue = this.calculateDepositValue;
    this.calculateQuote();
    this.debouncedCalcQuote = debounce(this.calculateQuote, 800);
  },
  methods: {
    formatMoney() {
      const value = event.target.value.split(",").join("");
      this.shownValue = formatCurrency(value);
      this.depositValue = value;
    },
    paymentChangeHandler(type) {
      if (!this.appliesToPCP && type === this.availableOptions.PCP) return null;
      this.paymentType = type;
      this.calculateQuote();
    },
    hpTermsChangeHandler(term) {
      this.hpTerm = term;
      this.calculateQuote();
    },
    calculateQuote() {
      usedCarsAPI()
        .post(CALCULATE_FINANCE_QUOTE(this.carId), this.prepareData)
        .then(response => {
          this.calculatedData = response.data;
          this.errorMessage = "";
          this.applyForFinance();
        })
        .catch(error => {
          this.submitStatus = apiResponseMessages.ERROR;
          this.errorMessage = error.response.data;
        });
    },
    applyForFinance() {
      this.submitStatus = apiResponseMessages.PENDING;
      usedCarsAPI()
        .post(SEND_FINANCE_QUOTE(this.carId), this.prepareData)
        .then(response => {
          this.submitStatus = apiResponseMessages.OK;
          this.iFrameLink = response.data.url;
        })
        .catch(error => {
          this.submitStatus = apiResponseMessages.ERROR;
          this.errorMessage = error.response.data;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.quote-form {
  position: relative;

  &__select {
    margin: 10px 0;
  }
}

.quote-field {
  padding: 16px 19px;
  border: 1px solid $color-grey-100;
  display: flex;
  align-items: center;
  margin: 10px 0;
  justify-content: space-between;

  &__description {
    color: $color-grey-300;
    white-space: nowrap;
    margin-right: 5px;
  }

  &__currency {
    @include absolute(left 195px top 50%);

    transform: translate(0, -50%);

    &--grey {
      color: $color-grey-300;
    }
  }

  &__value {
    width: 100%;
    border: 0;
    padding-left: 12px;
    min-width: 0;
  }

  &__label {
    position: relative;
    padding: 15px 19px;
    cursor: pointer;
  }

  &__instruction {
    font-size: $font-size-s;
  }

  &--error {
    border-color: $color-red-100;
    color: $color-red-200;

    .quote-field__description {
      color: $color-red-200;
    }

    .quote-field__value {
      color: $color-red-200;
    }
  }
}

.quote-info {
  margin-top: 24px;
  display: flex;

  &__item {
    @include flex-basis(50%);
  }

  &__number {
    font-size: $font-size-xl;
    color: $color-blue-300;
    display: flex;
  }

  &__tooltip {
    margin-left: 15px;
  }

  &__text {
    color: $color-grey-300;
    margin-top: 10px;
    padding-right: 10px;
  }
}

.quote-badge {
  margin-top: 30px;
}

.finance-quote__link {
  flex-basis: 50%;
}

.finance-quote__submit {
  margin-top: 10px;
}

.finance-quote__iframe {
  height: 70vh;
  width: 100%;
  overflow-y: visible;
  border: 2px solid $color-blue-300;
}

.quote-terms {
  margin-top: 26px;

  &__text {
    font-size: $font-size-m;
    color: $color-grey-300;
    margin-top: 16px;
    line-height: 19px;
    flex-direction: column;
    align-items: flex-start;
  }

  &__warning {
    margin: 6px 0;
    border: 2px solid black;
  }
}

.terms-list {
  list-style-type: circle;
  list-style-position: inside;
  font-size: $font-size-m;
  color: $color-grey-300;

  &__item {
    margin: 8px 0;
  }
}

@media screen and (min-width: $media-l) {
  .quote-form {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .quote-field {
    margin: 0;

    &__wrapper {
      @include flex-basis(20%);

      padding: 0 5px;
      box-sizing: border-box;

      &--label {
        @include flex-basis(40%);
      }
    }
  }

  .quote-info {
    @include flex-basis(30%);

    margin-top: 32px;
  }

  .quote-form__select {
    margin: 0;
  }

  .finance-quote__price {
    @include flex-basis(60%);

    padding-top: 32px;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
  }

  .quote-badge,
  .finance-quote__submit {
    @include flex-basis(50%);

    margin: 0 5px;
  }

  .quote-terms {
    display: flex;

    &__conditions {
      @include flex-basis(25%);
    }

    &__text {
      @include flex-basis(75%);

      padding-left: 60px;
      display: flex;
      align-items: flex-start;
    }
  }
}

@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  .quote-field__currency {
    top: 50%;
    left: 203px;
    transform: translate(0, -50%);
  }
}
</style>
